import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import MapModal from './MapModal';
import { fetchMaps } from '../services/api';
import SEOHead from './SEOHead';

// Region metadata
const regionInfo = {
  'dobrogea': {
    name: 'Dobrogea',
    subtitle: 'Colecția Dobrogeană',
    description: 'Hărți istorice ale regiunii Dobrogea, de la antichitate până în perioada modernă.',
    filterKeywords: ['dobrogea', 'dobruja', 'constanta', 'tulcea', 'pontus', 'scythia minor'],
    bgColor: 'from-stone-900 via-stone-800 to-amber-950',
  },
  'dacia': {
    name: 'Dacia',
    subtitle: 'Imperiul Geto-Dac',
    description: 'Hărți ale Daciei antice și teritoriilor geto-dacice.',
    filterKeywords: ['dacia', 'dacian', 'getic', 'getae'],
    bgColor: 'from-amber-950 via-stone-900 to-stone-800',
  },
  'imperiul-otoman': {
    name: 'Imperiul Otoman',
    subtitle: 'Epoca Fanariotă',
    description: 'Hărți ale Imperiului Otoman și teritoriilor sub dominație turcească.',
    filterKeywords: ['ottoman', 'turci', 'turc', 'turkey', 'turquie', 'otoman', 'turkish'],
    bgColor: 'from-stone-800 via-red-950 to-stone-900',
  },
  'imperiul-roman': {
    name: 'Imperiul Roman',
    subtitle: 'Pax Romana',
    description: 'Hărți ale Imperiului Roman și provinciilor sale.',
    filterKeywords: ['roman', 'rome', 'roma', 'imperium', 'romanum'],
    bgColor: 'from-red-950 via-stone-900 to-amber-950',
  },
  'valachia': {
    name: 'Valachia',
    subtitle: 'Țara Românească',
    description: 'Hărți ale Valahiei și Țării Românești.',
    filterKeywords: ['valachia', 'wallachia', 'walachia', 'muntenia', 'valahia'],
    bgColor: 'from-stone-900 via-amber-950 to-stone-800',
  },
  'bulgaria': {
    name: 'Bulgaria',
    subtitle: 'Regatul Balcanic',
    description: 'Hărți istorice ale Bulgariei și regiunii balcanice.',
    filterKeywords: ['bulgaria', 'bulgar', 'balkan'],
    bgColor: 'from-emerald-950 via-stone-900 to-stone-800',
  },
  'grecia-antica': {
    name: 'Grecia Antică',
    subtitle: 'Hellada Eternă',
    description: 'Hărți ale Greciei antice și civilizației elenistice.',
    filterKeywords: ['greece', 'greek', 'grecia', 'hellas', 'hellenic', 'ancient greece'],
    bgColor: 'from-blue-950 via-stone-900 to-stone-800',
  },
  'europa': {
    name: 'Europa',
    subtitle: 'Hărți Continentale',
    description: 'Hărți ale continentului european de-a lungul istoriei.',
    filterKeywords: ['europe', 'europa', 'european', 'continent'],
    bgColor: 'from-stone-800 via-stone-900 to-amber-950',
  },
};

const CategoryView = () => {
  const { regionId } = useParams();
  const navigate = useNavigate();
  const [maps, setMaps] = useState([]);
  const [filteredMaps, setFilteredMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const region = regionInfo[regionId];

  useEffect(() => {
    const loadMaps = async () => {
      setIsLoading(true);
      try {
        const data = await fetchMaps();
        setMaps(data);
        
        // Filter maps based on region keywords
        if (region) {
          const filtered = data.filter(map => {
            const searchText = `${map.title} ${map.region} ${map.description || ''}`.toLowerCase();
            return region.filterKeywords.some(keyword => searchText.includes(keyword.toLowerCase()));
          });
          setFilteredMaps(filtered);
        }
      } catch (error) {
        console.error('Error loading maps:', error);
      }
      setIsLoading(false);
    };

    loadMaps();
  }, [regionId, region]);

  if (!region) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Regiune negăsită</h1>
          <button 
            onClick={() => navigate('/')}
            className="text-amber-500 hover:text-amber-400"
          >
            Înapoi la pagina principală
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950">
      <SEOHead
        title={region ? `${region.name} — Hărți Istorice | Casa Cartarum` : 'Colecție Hărți | Casa Cartarum'}
        description={region ? `${region.description} Colecție de hărți rare din arhiva Casa Cartarum.` : 'Colecție de hărți istorice rare.'}
        canonical={region ? `https://www.casacartarum.ro/category/${regionId}` : 'https://www.casacartarum.ro/'}
      />
      <Navbar 
        onOpenExplorer={() => navigate('/')} 
        onOpenAudio={() => {}}
      />

      {/* Hero Header */}
      <div className={`relative py-20 md:py-28 bg-gradient-to-br ${region.bgColor}`}>
        {/* Texture overlay */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />
        
        {/* Vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-black/30" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-amber-200/70 hover:text-amber-200 mb-8 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span>Înapoi la arhivă</span>
          </button>

          {/* Title */}
          <div className="flex items-center gap-4 mb-4">
            <div className="h-px w-12 bg-amber-600/50" />
            <div className="w-2 h-2 rotate-45 bg-amber-600/50" />
          </div>
          
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          >
            {region.name}
          </h1>
          
          <p className="text-xl text-amber-200/80 mb-2">{region.subtitle}</p>
          <p className="text-lg text-white/60 max-w-2xl">{region.description}</p>
          
          {/* Map count */}
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg">
            <span className="text-amber-400 font-bold text-xl">{filteredMaps.length}</span>
            <span className="text-white/70">hărți în colecție</span>
          </div>
        </div>
      </div>

      {/* Maps Grid */}
      <div className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
            </div>
          ) : filteredMaps.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/60 text-lg">Nu au fost găsite hărți pentru această regiune.</p>
              <button 
                onClick={() => navigate('/')}
                className="mt-4 text-amber-500 hover:text-amber-400"
              >
                Explorează alte regiuni
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredMaps.map((map) => (
                <div
                  key={map.id}
                  className="group relative bg-stone-900 rounded-lg overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-900/20"
                  onClick={() => setSelectedMap(map)}
                  data-testid={`map-card-${map.id}`}
                >
                  {/* Map Image */}
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={map.imageUrl}
                      alt={map.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>

                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  {/* Year badge */}
                  {map.year && (
                    <div className="absolute top-3 left-3 px-2 py-1 bg-amber-600/90 text-white text-xs font-medium rounded">
                      {map.year}
                    </div>
                  )}

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 
                      className="text-white font-semibold text-sm md:text-base line-clamp-2 mb-1"
                      style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
                    >
                      {map.title}
                    </h3>
                    {map.author && (
                      <p className="text-amber-200/70 text-xs">{map.author}</p>
                    )}
                    {map.region && (
                      <p className="text-white/50 text-xs mt-1">{map.region}</p>
                    )}
                  </div>

                  {/* Hover border effect */}
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-amber-600/50 rounded-lg transition-colors duration-300" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Map Modal */}
      {selectedMap && (
        <MapModal
          map={selectedMap}
          onClose={() => setSelectedMap(null)}
          onPrevious={() => {
            const currentIndex = filteredMaps.findIndex(m => m.id === selectedMap.id);
            if (currentIndex > 0) {
              setSelectedMap(filteredMaps[currentIndex - 1]);
            }
          }}
          onNext={() => {
            const currentIndex = filteredMaps.findIndex(m => m.id === selectedMap.id);
            if (currentIndex < filteredMaps.length - 1) {
              setSelectedMap(filteredMaps[currentIndex + 1]);
            }
          }}
        />
      )}

      <Footer />
    </div>
  );
};

export default CategoryView;
